import { useEffect, useMemo, useRef, useState } from "react";
import { css } from "@emotion/react";
import Tooltip from "../Tooltip";
import { TruncatedProps } from "./types";
import { styles } from "./styles";

export const Truncated = ({
  text,
  lines = 1,
  tooltip = true,
  offset
}: TruncatedProps) => {
  const intersectionRoot = useRef<HTMLDivElement | null>(null);
  const overflowIndicator = useRef<HTMLSpanElement | null>(null);
  const [showTooltip, setShowTooltip] = useState(tooltip);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const [base, suffix] = useMemo<[string, string]>(() => {
    if (lines > 1 || !offset || offset >= text.length) {
      return [text, ""];
    }
    return [text.slice(0, text.length - offset), text.slice(offset * -1)];
  }, [text, lines, offset]);

  useEffect(() => {
    if (!overflowIndicator.current || (!tooltip && !offset)) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.target === overflowIndicator.current) {
            if (tooltip) {
              setShowTooltip(!entry.intersectionRatio);
            }
            setIsOverflowing(!entry.intersectionRatio);
          }
        });
      },
      { root: intersectionRoot.current }
    );

    observer.observe(overflowIndicator.current);

    return () => {
      if (overflowIndicator.current && observer) {
        observer.unobserve(overflowIndicator.current);
        observer.disconnect();
      }
    };
  }, [overflowIndicator.current, text, lines, tooltip]);

  return (
    <div
      ref={intersectionRoot}
      className="kit-Truncated"
      css={[
        css`
          --truncate-lines: ${lines};
        `,
        lines === 1 ? styles.singleLine : styles.multiLine
      ]}
    >
      <span css={styles.offsetBase}>
        {base}
        <span css={styles.overflowIndicator} ref={overflowIndicator} />
      </span>
      {!!suffix && (
        <span css={isOverflowing && styles.suffixWithOverflow}>{suffix}</span>
      )}

      {/* Blueprint Popover adds a bunch of extra nested elements which causes problems with the styling
    so we render it with a fake element that overlays the real text. */}
      {showTooltip && (
        <div css={styles.tooltip}>
          <Tooltip
            content={text}
            position="top"
            {...(typeof tooltip === "object" ? tooltip : {})}
          >
            <span />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export default Truncated;
export * from "./types";
